// src/pages/medical-centers/MedicalCenterSelectionPage.jsx
import React, { useEffect, useState } from 'react';
import { useMedicalCenter } from '../../context/MedicalCenterContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useAlert } from '../../context/AlertContext';
import { medicalCenterService } from '../../services/medicalCenterService';

import PageHeader from '../../components/common/PageHeader';
import Table from '../../components/common/Table';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import MedicalCenterForm from '../../components/forms/MedicalCenterForm';

const MedicalCenterSelectionPage = () => {
  const { availableMedicalCenters, selectMedicalCenter } = useMedicalCenter();
  const { canCreate, canEdit, canDelete } = usePermissions();
  const { push } = useAlert();

  // Local list for optimistic updates (no refetch)
  const [centers, setCenters] = useState([]);
  useEffect(() => {
    setCenters(availableMedicalCenters || []);
  }, [availableMedicalCenters]);

  // Auto-select when only one center is available (set context, do not navigate)
  useEffect(() => {
    const AUTO_KEY = 'mc:autoNav';
    if (centers.length === 1 && !sessionStorage.getItem(AUTO_KEY)) {
      sessionStorage.setItem(AUTO_KEY, '1');
      selectMedicalCenter(centers[0].id, { navigate: true });
    }
  }, [centers, selectMedicalCenter]);

  // Modals/dialogs
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [selectedMC, setSelectedMC] = useState(null);

  const openEdit = async (mc) => {
    try {
      setFormLoading(true);
      const fullMc = await medicalCenterService.getMedicalCenter(mc.id);
      setSelectedMC({
        ...mc,
        ...fullMc,
        programs: fullMc?.programs || mc.programs || [],
      });
      setShowEditModal(true);
    } catch (err) {
      push(`Failed to load medical center: ${err.message}`, 'error');
    } finally {
      setFormLoading(false);
    }
  };

  const openDelete = (mc) => {
    setSelectedMC(mc);
    setShowDeleteDialog(true);
  };

  // Create (optimistic: append after success)
  const handleCreate = async (formData) => {
    try {
      setFormLoading(true);
      const created = await medicalCenterService.createMedicalCenter(formData);
      setCenters((prev) => [
        ...prev,
        {
          ...created,
          programs: created.programs || [],
        },
      ]);
      setShowCreateModal(false);
      push('Medical center created', 'success');
    } catch (err) {
      push(`Failed to create medical center: ${err.message}`, 'error');
    } finally {
      setFormLoading(false);
    }
  };

  // Edit (optimistic: update in place)
  const handleEdit = async (formData) => {
    try {
      setFormLoading(true);
      const updated = await medicalCenterService.updateMedicalCenter(selectedMC.id, formData);
      setCenters((prev) =>
        prev.map((mc) =>
          mc.id === selectedMC.id
            ? {
                ...mc,
                ...updated,
                programs: updated.programs || mc.programs || [],
              }
            : mc
        )
      );
      setShowEditModal(false);
      setSelectedMC(null);
      push('Medical center updated', 'success');
    } catch (err) {
      push(`Failed to update medical center: ${err.message}`, 'error');
    } finally {
      setFormLoading(false);
    }
  };

  // Delete (optimistic: remove from list)
  const handleDeleteConfirm = async () => {
    try {
      setFormLoading(true);
      await medicalCenterService.deleteMedicalCenter(selectedMC.id);
      setCenters(prev => prev.filter(mc => mc.id !== selectedMC.id));
      setShowDeleteDialog(false);
      setSelectedMC(null);
      push('Medical center deleted', 'success');
    } catch (err) {
      push(`Failed to delete medical center: ${err.message}`, 'error');
    } finally {
      setFormLoading(false);
    }
  };

  const renderRow = (mc) => (
    <tr key={mc.id} className="hover:bg-gray-50">
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-700">
        <button
          className="hover:underline"
          onClick={() => selectMedicalCenter(mc.id)}
        >
          {mc.name || mc.id}
        </button>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {mc.address || '—'}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {mc.time_zone ? mc.time_zone.replace('_', ' ') : '—'}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
        <div className="flex space-x-2">
          {canEdit('medicalCenters') && (
            <Button size="sm" variant="outline" onClick={() => openEdit(mc)}>
              Edit
            </Button>
          )}
          {canDelete('medicalCenters') && (
            <Button size="sm" variant="danger" onClick={() => openDelete(mc)}>
              Delete
            </Button>
          )}
        </div>
      </td>
    </tr>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Select Medical Center"
        subtitle="Choose a center to continue"
        actionText={canCreate('medicalCenters') ? 'Add Medical Center' : undefined}
        onAction={canCreate('medicalCenters') ? () => setShowCreateModal(true) : undefined}
        showAction={canCreate('medicalCenters')}
      />

      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Medical Centers</h2>
        </div>
        <Table
          headers={['Name', 'Address', 'Time Zone', 'Actions']}
          data={centers}
          renderRow={renderRow}
          emptyMessage="No medical center access."
        />
      </div>

      {/* Create Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create Medical Center"
      >
        <MedicalCenterForm
          onSubmit={handleCreate}
          onCancel={() => setShowCreateModal(false)}
          loading={formLoading}
        />
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedMC(null);
        }}
        title="Edit Medical Center"
      >
        {selectedMC && (
          <MedicalCenterForm
            medicalCenter={selectedMC}
            onSubmit={handleEdit}
            onCancel={() => {
              setShowEditModal(false);
              setSelectedMC(null);
            }}
            loading={formLoading}
          />
        )}
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setSelectedMC(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Medical Center"
        message={`Are you sure you want to delete "${selectedMC?.name || selectedMC?.id}"?`}
        confirmText="Delete"
        cancelText="Cancel"
        loading={formLoading}
        variant="danger"
      />
    </div>
  );
};

export default MedicalCenterSelectionPage;